import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Tabs, Dropdown, Badge, Tooltip, message, Input } from 'antd';
import { FileTextOutlined, TagsOutlined, ExclamationCircleOutlined, ShoppingOutlined, DollarOutlined, WarningOutlined, SwapOutlined, BankOutlined, DatabaseOutlined, ApartmentOutlined, SkinOutlined, TransactionOutlined, SafetyCertificateOutlined, UnorderedListOutlined, MedicineBoxOutlined, TeamOutlined, FormOutlined, WalletOutlined, InsuranceOutlined, AlertOutlined, FolderOpenOutlined, ToolOutlined, GiftOutlined, AccountBookOutlined, CalendarOutlined, ClockCircleOutlined, CreditCardOutlined, BarChartOutlined, RollbackOutlined, AuditOutlined, ScissorOutlined, OrderedListOutlined, GlobalOutlined, ExperimentOutlined, SolutionOutlined, BgColorsOutlined, FieldTimeOutlined, CheckCircleOutlined, NotificationOutlined, FileExclamationOutlined, SearchOutlined, CalculatorOutlined, StarOutlined, StarFilled, BugOutlined, ClockCircleFilled, CheckCircleFilled, MoreOutlined, CloseCircleOutlined, RightOutlined, ScheduleOutlined, IdcardOutlined, SafetyOutlined, FileSearchOutlined} from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import ModuleCategoryView from './ModuleCategoryView';
import { modules, ERPModule } from './erpModules';
import { erpHomeApi, DevModuleStatus, ModuleTestStatus, StatusMap } from '../../services/erpHomeApi';
import { bugReportApi } from '../../services/bugReportApi';
import type { BugReport } from '../../components/BugReport/types';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_CONFIG: Record<ModuleTestStatus, { bg: string; border: string; label: string; icon: React.ReactNode }> = {
  untested:  { bg: '#ffffff', border: '#d9d9d9', label: '未測試', icon: <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#d9d9d9', marginRight: 4 }} /> },
  has_bug:   { bg: '#fff1f0', border: '#ffa39e', label: '待修復', icon: <BugOutlined style={{ color: '#cf1322', marginRight: 4 }} /> },
  bug_fixed: { bg: '#fffbe6', border: '#ffe58f', label: '待驗收', icon: <ClockCircleFilled style={{ color: '#d48806', marginRight: 4 }} /> },
  verified:  { bg: '#f6ffed', border: '#b7eb8f', label: '已完成', icon: <CheckCircleFilled style={{ color: '#389e0d', marginRight: 4 }} /> },
};

const CARD_STATUS_TO_DEV_STATUS: Record<ModuleTestStatus, DevModuleStatus> = {
  untested: '--',
  has_bug: 'bug',
  bug_fixed: 'wip',
  verified: 'done',
};

const DEV_STATUS_TO_CARD_STATUS: Record<DevModuleStatus, ModuleTestStatus> = {
  '--': 'untested',
  wip: 'bug_fixed',
  done: 'verified',
  bug: 'has_bug',
  blocked: 'untested',
};

const DEV_FORM_ID_OVERRIDES: Record<string, string> = {
  sc: 'pwc202f1',
  c5m5: 'pwc5m5f1',
};

const extractDevFormId = (module: ERPModule): string | null => {
  const override = DEV_FORM_ID_OVERRIDES[module.key];
  if (override) {
    return override;
  }

  const match = module.description.match(/\(([^()]+)\)\s*$/);
  return match ? match[1].trim() : null;
};


const MODULE_DEV_FORM_IDS: Record<string, string> = modules.reduce((acc, module) => {
  const formId = extractDevFormId(module);
  if (formId) {
    acc[module.key] = formId;
  }
  return acc;
}, {} as Record<string, string>);

const DISMISSED_KEY = 'erp_myreports_dismissed';

const loadDismissed = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
};

const saveDismissed = (set: Set<string>) => {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(set)));
};

const BUG_STATUS_DOT: Record<string, { color: string; label: string; bg: string; textColor: string }> = {
  open:        { color: '#cf1322', label: '待修復', bg: '#fff1f0', textColor: '#cf1322' },
  in_progress: { color: '#fa8c16', label: '修復中', bg: '#fff7e6', textColor: '#d46b08' },
  resolved:    { color: '#d48806', label: '待驗收', bg: '#fffbe6', textColor: '#ad6800' },
  closed:      { color: '#389e0d', label: '已完成', bg: '#f6ffed', textColor: '#237804' },
};

const ERPHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusMap, setStatusMap] = useState<StatusMap>({});
  const [devStatusMap, setDevStatusMap] = useState<Record<string, DevModuleStatus>>({});
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [myBugs, setMyBugs] = useState<BugReport[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    try {
      const [statuses, favs, devModules] = await Promise.all([
        erpHomeApi.getStatuses(),
        erpHomeApi.getFavorites(),
        erpHomeApi.getDevModules(),
      ]);
      setStatusMap(statuses || {});
      setFavorites(new Set(favs || []));
      setDevStatusMap((devModules || []).reduce((acc, module) => {
        acc[module.formId] = module.status;
        return acc;
      }, {} as Record<string, DevModuleStatus>));
    } catch {
      // silently fail on initial load
    }
  }, []);

  const fetchMyBugs = useCallback(async () => {
    const reportedBy = user?.userName || user?.userId;
    if (!reportedBy) return;
    try {
      const result = await bugReportApi.list({ reportedBy, size: 100 });
      setMyBugs(result.data || []);
    } catch {
      // silently fail
    }
  }, [user?.userName, user?.userId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMyBugs(); }, [fetchMyBugs]);

  const handleStatusChange = useCallback(async (key: string, status: ModuleTestStatus) => {
    const formId = MODULE_DEV_FORM_IDS[key];
    const nextDevStatus = CARD_STATUS_TO_DEV_STATUS[status];

    try {
      const [erpStatus, devModule] = await Promise.all([
        erpHomeApi.updateStatus(key, status),
        formId ? erpHomeApi.updateDevModuleStatus(formId, nextDevStatus) : Promise.resolve(null),
      ]);

      setStatusMap((prev) => ({ ...prev, [key]: erpStatus }));
      if (devModule?.formId) {
        setDevStatusMap((prev) => ({ ...prev, [devModule.formId]: devModule.status }));
      }
      message.success(`${STATUS_CONFIG[status].label}`);
    } catch {
      message.error('更新狀態失敗');
    }
  }, []);

  const handleToggleFavorite = useCallback(async (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await erpHomeApi.toggleFavorite(key);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (result.action === 'added') next.add(key);
        else next.delete(key);
        return next;
      });
    } catch {
      message.error('更新常用失敗');
    }
  }, []);

  const getModuleStatus = (module: ERPModule): ModuleTestStatus => {
    const erpStatus = statusMap[module.key]?.status as ModuleTestStatus | undefined;
    if (erpStatus === 'has_bug' || erpStatus === 'bug_fixed') {
      return erpStatus;
    }

    const formId = MODULE_DEV_FORM_IDS[module.key];
    if (formId) {
      const devStatus = devStatusMap[formId];
      if (devStatus) {
        return DEV_STATUS_TO_CARD_STATUS[devStatus];
      }
    }

    return erpStatus || 'untested';
  };

  const statusCounts = useMemo(() => {
    const counts = { untested: 0, has_bug: 0, bug_fixed: 0, verified: 0 };
    modules.forEach((m) => { counts[getModuleStatus(m)]++; });
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusMap, devStatusMap]);

  const filteredModules = useMemo(() => {
    let list = activeTab === 'favorites'
      ? modules.filter((m) => favorites.has(m.key))
      : modules;
    if (searchText.trim()) {
      const kw = searchText.trim().toLowerCase();
      list = list.filter((m) =>
        m.key.toLowerCase().includes(kw) ||
        m.title.toLowerCase().includes(kw) ||
        m.description.toLowerCase().includes(kw) ||
        (MODULE_DEV_FORM_IDS[m.key] || '').toLowerCase().includes(kw)
      );
    }
    return list;
  }, [activeTab, favorites, searchText]);

  // Group bugs by page path for "我的回報" tab
  const myReportGroups = useMemo(() => {
    const grouped: Record<string, BugReport[]> = {};
    for (const bug of myBugs) {
      if (!grouped[bug.page]) grouped[bug.page] = [];
      grouped[bug.page].push(bug);
    }
    return Object.entries(grouped)
      .filter(([path, bugs]) => {
        // All bugs closed (已驗收) → naturally hide, no need to show
        if (bugs.every((b) => b.status === 'closed')) return false;
        // Manual X dismiss → hide, but re-show if new open bugs appear
        if (dismissed.has(path)) {
          return bugs.some((b) => b.status === 'open' || b.status === 'in_progress');
        }
        return true;
      })
      .sort((a, b) => {
        // Sort: has open bugs first
        const aOpen = a[1].some((b) => b.status === 'open' || b.status === 'in_progress');
        const bOpen = b[1].some((b) => b.status === 'open' || b.status === 'in_progress');
        if (aOpen !== bOpen) return aOpen ? -1 : 1;
        return b[1][0].createdAt.localeCompare(a[1][0].createdAt);
      });
  }, [myBugs, dismissed]);

  const handleDismiss = useCallback((path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(path);
      saveDismissed(next);
      return next;
    });
  }, []);

  const toggleExpand = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const statusMenuItems = (key: string): { key: string; label: React.ReactNode }[] =>
    (['untested', 'has_bug', 'bug_fixed', 'verified'] as ModuleTestStatus[]).map((s) => ({
      key: s,
      label: (
        <span onClick={(e) => { e.stopPropagation(); handleStatusChange(key, s); }}>
          {STATUS_CONFIG[s].icon} {STATUS_CONFIG[s].label}
        </span>
      ),
    }));

  const renderCard = (m: typeof modules[0]) => {
    const status = getModuleStatus(m);
    const cfg = STATUS_CONFIG[status];
    const isFav = favorites.has(m.key);

    return (
      <Col key={m.key} xs={12} sm={8} md={6} lg={4}>
        <Card
          hoverable
          onClick={() => navigate(m.path)}
          size="small"
          style={{
            textAlign: 'center',
            height: 120,
            overflow: 'hidden',
            background: cfg.bg,
            borderColor: cfg.border,
            position: 'relative',
          }}
        >
          {/* Favorite star */}
          <Tooltip title={isFav ? '取消常用' : '加入常用'}>
            <span
              onClick={(e) => handleToggleFavorite(m.key, e)}
              style={{
                position: 'absolute', top: 4, left: 6, cursor: 'pointer',
                fontSize: 14, zIndex: 1, lineHeight: 1,
              }}
            >
              {isFav
                ? <StarFilled style={{ color: '#faad14' }} />
                : <StarOutlined style={{ color: '#d9d9d9' }} />}
            </span>
          </Tooltip>

          {/* Status dropdown */}
          <Dropdown menu={{ items: statusMenuItems(m.key) }} trigger={['click']} placement="bottomRight">
            <span
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 4, right: 6, cursor: 'pointer',
                fontSize: 14, zIndex: 1, lineHeight: 1,
              }}
            >
              {status === 'untested'
                ? <MoreOutlined style={{ color: '#bbb' }} />
                : STATUS_CONFIG[status].icon}
            </span>
          </Dropdown>

          <div style={{ marginBottom: 8, color: '#1677ff' }}>{m.icon}</div>
          <Card.Meta
            title={<span style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{m.title}</span>}
            description={<span style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{m.description}</span>}
          />
        </Card>
      </Col>
    );
  };

  const findModuleByPath = (path: string) => modules.find((m) => m.path === path);

  const renderMyReports = () => {
    if (myReportGroups.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          <BugOutlined style={{ fontSize: 32, marginBottom: 8 }} /><br />
          目前沒有需要追蹤的回報
          <div style={{ marginTop: 8, fontSize: 13 }}>
            新回報會出現在這裡，修復完成後也會保留到你完成驗收為止
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {myReportGroups.map(([path, bugs]) => {
          const mod = findModuleByPath(path);
          const openCount = bugs.filter((b) => b.status === 'open' || b.status === 'in_progress').length;
          const resolvedCount = bugs.filter((b) => b.status === 'resolved').length;
          const closedCount = bugs.filter((b) => b.status === 'closed').length;
          const isExpanded = expandedPaths.has(path);
          const title = mod?.title || bugs[0]?.pageTitle || path;
          const summaryParts = [
            openCount > 0 ? `待修復 ${openCount} 筆` : null,
            resolvedCount > 0 ? `待驗收 ${resolvedCount} 筆` : null,
            closedCount > 0 ? `已完成 ${closedCount} 筆` : null,
          ].filter(Boolean);
          const actionLabel = openCount > 0 ? '前往查看' : '前往驗收';

          return (
            <div key={path}>
              <Card
                hoverable
                size="small"
                onClick={() => toggleExpand(path)}
                style={{ borderColor: openCount > 0 ? '#ffa39e' : resolvedCount > 0 ? '#ffe58f' : '#b7eb8f', background: openCount > 0 ? '#fff1f0' : resolvedCount > 0 ? '#fffbe6' : '#f6ffed' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 20, color: '#1677ff', flexShrink: 0 }}>
                    {mod?.icon || <BugOutlined />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{title}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{mod?.description || path}</div>
                    {summaryParts.length > 0 && (
                      <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
                        {summaryParts.join(' · ')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                    {openCount > 0 && (
                      <Badge count={openCount} style={{ backgroundColor: '#ff4d4f' }} title="待修復" />
                    )}
                    {resolvedCount > 0 && (
                      <Badge count={resolvedCount} style={{ backgroundColor: '#fa8c16' }} title="待驗收" />
                    )}
                  </div>
                  <span
                    onClick={(e) => { e.stopPropagation(); navigate(path); }}
                    style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {actionLabel} <RightOutlined style={{ fontSize: 10 }} />
                  </span>
                  <Tooltip title="從清單隱藏">
                    <CloseCircleOutlined
                      onClick={(e) => handleDismiss(path, e)}
                      style={{ color: '#bbb', fontSize: 14, flexShrink: 0 }}
                    />
                  </Tooltip>
                  <RightOutlined style={{ color: '#bbb', fontSize: 12, transform: isExpanded ? 'rotate(90deg)' : undefined, transition: 'transform 0.2s', flexShrink: 0 }} />
                </div>
              </Card>
              {isExpanded && (
                <div style={{ marginLeft: 32, marginTop: 4, marginBottom: 8 }}>
                  {bugs.map((bug) => {
                    const statusInfo = BUG_STATUS_DOT[bug.status] || { color: '#d9d9d9', label: bug.status, bg: '#fafafa', textColor: '#595959' };
                    const desc = bug.annotations?.[0]?.description || '(無描述)';
                    return (
                      <div key={bug.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <Tooltip title={statusInfo.label}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: statusInfo.color, marginTop: 5, flexShrink: 0 }} />
                        </Tooltip>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13 }}>{desc}</div>
                            <span
                              style={{
                                fontSize: 11,
                                padding: '1px 8px',
                                borderRadius: 999,
                                background: statusInfo.bg,
                                color: statusInfo.textColor,
                                fontWeight: 500,
                              }}
                            >
                              {statusInfo.label}
                            </span>
                          </div>
                          {bug.resolution && (
                            <div style={{ fontSize: 12, color: '#237804', marginTop: 4 }}>修復說明：{bug.resolution}</div>
                          )}
                          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                            回報時間：{new Date(bug.createdAt).toLocaleDateString('zh-TW')}
                          </div>
                        </div>
                        <Link to={path} style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          查看頁面
                        </Link>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>ERP 模組</h2>
        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
          {(['untested', 'has_bug', 'bug_fixed', 'verified'] as ModuleTestStatus[]).map((s) => (
            <span key={s} style={{ display: 'flex', alignItems: 'center' }}>
              {STATUS_CONFIG[s].icon}
              <span>{STATUS_CONFIG[s].label}</span>
              <Badge count={statusCounts[s]} showZero style={{ marginLeft: 4, backgroundColor: s === 'has_bug' ? '#ff4d4f' : s === 'bug_fixed' ? '#faad14' : s === 'verified' ? '#52c41a' : '#d9d9d9' }} />
            </span>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Input
          placeholder="搜尋 FormID 或名稱..."
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          allowClear
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'all', label: `全部模組 (${modules.length})` },
          { key: 'categories', label: <span><OrderedListOutlined style={{ marginRight: 4 }} />模組分類</span> },
          { key: 'favorites', label: <span><StarFilled style={{ color: '#faad14', marginRight: 4 }} />我的常用 ({favorites.size})</span> },
          { key: 'myreports', label: <span><BugOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />我的回報{myReportGroups.length > 0 ? ` (${myReportGroups.length})` : ''}</span> },
        ]}
        style={{ marginBottom: 16 }}
      />

      {activeTab === 'categories' ? (
        <ModuleCategoryView modules={modules} searchText={searchText} getModuleStatus={getModuleStatus} onStatusChange={handleStatusChange} favorites={favorites} onToggleFavorite={(key) => { erpHomeApi.toggleFavorite(key).then(r => setFavorites(prev => { const next = new Set(prev); if (r.action === 'added') next.add(key); else next.delete(key); return next; })); }} />
      ) : activeTab === 'myreports' ? (
        renderMyReports()
      ) : activeTab === 'favorites' && favorites.size === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          <StarOutlined style={{ fontSize: 32, marginBottom: 8 }} /><br />
          點擊模組卡片左上角的星號，加入常用模組
        </div>
      ) : (
        <Row gutter={[12, 12]}>
          {filteredModules.map(renderCard)}
        </Row>
      )}
    </div>
  );
};

export default ERPHome;
